<script setup lang="ts">
import RlImageThumb from '@/components/common/RlImageThumb.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerPartDamages } from '@/composables/rc/miner-part-damages'
import { usePartDetails } from '@/composables/rc/part-details'
import { NotifyFail } from '@/helpers/core/notifications'
import { PartDamageType, type IDetailsPart } from '@/types/rc/miners'
import { Icon } from '@iconify/vue'
import { first } from 'lodash'
import { computed, ref, toRaw, useTemplateRef, watchEffect } from 'vue'

// BE Context
const { minerId, selectedPart } = useRepairsContext()
const { hasDamages, partDamages, partId, partType, movedToInventory, isReplacement } = usePartDetails(
  minerId,
  selectedPart
)

// UI state
const damagesToggle = ref(hasDamages.value)
const damages = ref<IDetailsPart['damages']>([])
const damageBeingDeleted = ref<number>()

// If the last damage doesn't have an id, we're currently adding a damage
const addingDamage = computed(() => !!damages.value.length && !damages.value[damages.value.length - 1].id)

// Select source values
const damagesOptions = Object.values(PartDamageType)

// Source state from BE - watch out for refetchOnWindowFocus
watchEffect(() => {
  damagesToggle.value = hasDamages.value
  damages.value = partDamages.value ? structuredClone(toRaw(partDamages.value)) : []
})

const fileInputArrayRef = useTemplateRef<HTMLInputElement[]>('fileInput')

const { createPartDamageMutation, deletePartDamageMutation } = useMinerPartDamages(minerId)
const { isPending: partDamageCreationPending } = createPartDamageMutation

const onButtonClick = (idx: number) => {
  fileInputArrayRef.value?.[idx]?.click()
}

// This triggers creation
const onUploadPictures = (e: Event, dmgIdx: number) => {
  if (!partId.value) return

  const target = e.target as HTMLInputElement
  const files = Array.from(target.files || [])

  const validFiles = files.filter((file) => {
    const isValidType = ['image/jpeg', 'image/png'].includes(file.type)
    const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
    return isValidType && isValidSize
  })

  if (validFiles.length < files.length) {
    NotifyFail('Invalid files selected, only png/jpeg images up to 10MB')
    return
  }

  createPartDamageMutation.mutate(
    {
      part_id: partId.value,
      part_type: partType.value,
      damage_type: damages.value[dmgIdx].type,
      description: damages.value[dmgIdx].description,
      pictures: files,
    },
    {
      onSettled: () => {
        // Reset so selecting the same files again still fires `change`
        target.value = ''
      },
    }
  )
}

const onDeleteDamage = (dmgIdx: number) => {
  if (!partId.value) return

  // Deletion state
  damageBeingDeleted.value = dmgIdx

  // This is BE-sourced, mutate
  if (damages.value[dmgIdx].id)
    deletePartDamageMutation.mutate(
      {
        part_id: partId.value,
        part_type: partType.value,
        damage_id: damages.value[dmgIdx].id,
      },
      {
        onSettled: () => {
          damageBeingDeleted.value = undefined
        },
      }
    )
  // This is in transit of being created, just discard
  else damages.value.splice(dmgIdx, 1)
}
</script>

<template>
  <!-- Switch. -->
  <div v-if="partId" class="flex gap-2">
    <el-switch v-model="damagesToggle" :disabled="hasDamages || movedToInventory || isReplacement" />
    <el-text class="text-text-component-label">Damages</el-text>
  </div>

  <template v-if="damagesToggle">
    <!-- Use the index to refer back to the item within the state array -->
    <div v-for="(dmg, idx) in damages" :key="dmg.id" class="w-full flex flex-col gap-2 items-start">
      <!-- Header -->
      <div class="w-full flex justify-between group">
        <p class="w-max text-text-component-label">Damage {{ idx + 1 }}</p>
        <Icon v-if="damageBeingDeleted == idx" icon="eos-icons:three-dots-loading" @click="onDeleteDamage(idx)" />
        <Icon
          v-else-if="!movedToInventory"
          class="invisible group-hover:visible cursor-pointer text-text-component-label"
          icon="mdi:trash-can-outline"
          @click="onDeleteDamage(idx)"
        />
      </div>

      <!-- Damage type select -->
      <el-select v-model="damages[idx].type" :disabled="!!damages[idx].id" @change="(opt) => (damages[idx].type = opt)">
        <el-option v-for="dmgOpt of damagesOptions" :key="dmgOpt" :value="dmgOpt" :label="dmgOpt" />
      </el-select>

      <!-- If 'Other', it gets additional input -->
      <el-input
        v-if="dmg.type === PartDamageType.Other"
        v-model="damages[idx].description"
        :disabled="!!damages[idx].id"
      />

      <!-- Display pictures -->
      <div class="grid grid-cols-3 gap-4 w-full">
        <RlImageThumb
          v-for="picture in dmg.pictures"
          :key="picture.picture_url"
          class-name="w-24 h-24"
          :url="picture.picture_url"
        />
      </div>

      <!-- Hidden file input, triggers mutation -->
      <input
        ref="fileInput"
        class="hidden"
        type="file"
        accept=".jpg,.jpeg,.png"
        multiple
        @change="onUploadPictures($event, idx)"
      />

      <!-- Button, triggers the input -->
      <el-button
        v-if="!dmg.id"
        class="bg-fill-primary w-max px-4 py-3 text-text-secondary"
        :loading="partDamageCreationPending"
        :disabled="partDamageCreationPending"
        @click="onButtonClick(idx)"
        >Upload image(s)</el-button
      >
    </div>

    <!-- Add damage text button -->
    <el-link
      class="self-start"
      type="primary"
      :disabled="addingDamage || movedToInventory"
      @click="damages.push({ type: first(damagesOptions)!, description: '', pictures: [] })"
    >
      <Icon icon="mdi:plus" :height="20" /> Add damage
    </el-link>
  </template>
</template>
